import clsx from 'clsx';
import React from 'react';
import Button from '../../atoms/Button/Button';
import ListSimpleDropdown from '../ListSimpleDropdown/ListSimpleDropdown';
import Popover from '../Popover/Popover';
import type { OptionsDropdownMenuProps } from './types';
import useOptionsDropdownMenu from './useOptionsDropdownMenu';

/**
 * OptionsDropdownMenu — v2 molecule.
 *
 * Menú de acciones tras un trigger de tres puntos (`more-bullets`). Reemplaza
 * al v1 `MoreIconMenu`: el trigger es un `Button` `plain`/`isIconOnly`, el panel
 * es el `Popover` v2 (`usePopover`) y el contenido la lista `ListSimpleDropdown`.
 *
 * Pensado para vivir dentro de cards clicables: el trigger frena la propagación
 * del click al card padre. Al activar una opción el foco vuelve al trigger (APG),
 * salvo que la opción mantenga el menú abierto (submenús / date-pickers).
 *
 * Accesibilidad (patrón APG "menu button"):
 *  - El trigger expone `aria-haspopup="menu"` y `aria-expanded`.
 *  - Al abrir con opciones, el foco va directo al primer `menuitem` del
 *    `ListSimpleDropdown` (flechas, Home/End y type-ahead operativos desde el
 *    primer keystroke). Sin opciones (solo `children`), se enfoca el panel.
 */
function OptionsDropdownMenu({
  ariaLabel,
  children,
  closeRelatedDropDowns,
  isVertical = false,
  options = [],
  shouldPreventEvent = false,
}: Readonly<OptionsDropdownMenuProps>): JSX.Element {
  const {
    anchorEl,
    isOpen,
    onClickInOption,
    onDismiss,
    onTriggerClick,
    resolvedAriaLabel,
    shouldCloseOnTabOut,
    shouldFocusPanelOnOpen,
    stopPropagationOnChildrenClick,
    triggerProps,
  } = useOptionsDropdownMenu({ ariaLabel, closeRelatedDropDowns, options });

  return (
    <div className={clsx('options-dropdown-menu', 'more-icon-menu', { 'more-icon-menu--open': isOpen, 'options-dropdown-menu--open': isOpen })}>
      <Button
        {...triggerProps}
        ariaLabel={resolvedAriaLabel}
        className={clsx('options-dropdown-menu__trigger', 'more-icon-menu__shadow', {
          'more-icon-menu__shadow--open': isOpen,
          'more-icon-menu__shadow--vertical': isVertical,
          'options-dropdown-menu__trigger--open': isOpen,
          'options-dropdown-menu__trigger--vertical': isVertical,
        })}
        isIconOnly
        leftIcon="more-bullets"
        onClick={onTriggerClick}
        variant="plain"
      />
      <Popover
        anchorEl={anchorEl}
        className="options-dropdown-menu__panel lesson-element-item__dropdown-lib"
        handleClose={onDismiss}
        open={isOpen}
        shouldCloseOnTabOut={shouldCloseOnTabOut}
        shouldFocusOnOpen={shouldFocusPanelOnOpen}
      >
        <ListSimpleDropdown items={options} onClick={onClickInOption} preventEvent={shouldPreventEvent} shouldAutoFocusFirstItem={!shouldFocusPanelOnOpen} />
        {children && (
          // Wrapper sin semántica (role="none", sinónimo ARIA de presentation): solo aísla los clicks
          // de los children del card padre. Sin onKeyDown: el keydown debe burbujear hasta `document`
          // para que el cierre por Escape de `usePopover` funcione con el foco dentro de los children.
          <div onClick={stopPropagationOnChildrenClick} role="none">
            {children}
          </div>
        )}
      </Popover>
    </div>
  );
}

export default OptionsDropdownMenu;
